import React, { useEffect, useMemo, useRef } from "react";
import styles from "../Graph.module.css";

export type NodeId = string;

export interface WeightedEdge {
  from: NodeId;
  to: NodeId;
  weight: number;
}

interface WeightedGraphEngineProps {
  nodes: NodeId[];
  edges: WeightedEdge[];
  startNode: NodeId | null;
  runSignal: number;
  resetSignal: number;
  highlightedNodes: NodeId[];
  shortestPathNodes: NodeId[];
  setHighlightedNodes: React.Dispatch<React.SetStateAction<NodeId[]>>;
  setDistances: React.Dispatch<
    React.SetStateAction<Record<NodeId, number>>
  >;
  setPrev: React.Dispatch<
    React.SetStateAction<Record<NodeId, NodeId | null>>
  >;
  setQueueSnapshot: React.Dispatch<React.SetStateAction<NodeId[]>>;
}

type Neighbor = { node: NodeId; weight: number };
type Adjacency = Record<NodeId, Neighbor[]>;

function buildAdjacency(nodes: NodeId[], edges: WeightedEdge[]): Adjacency {
  const adj: Adjacency = {};
  for (const n of nodes) adj[n] = [];

  for (const { from, to, weight } of edges) {
    if (!adj[from]) adj[from] = [];
    if (!adj[to]) adj[to] = [];

    adj[from].push({ node: to, weight });
    adj[to].push({ node: from, weight });
  }
  return adj;
}

const DijkstraEngine: React.FC<WeightedGraphEngineProps> = ({
  nodes,
  edges,
  startNode,
  runSignal,
  resetSignal,
  highlightedNodes,
  shortestPathNodes,
  setHighlightedNodes,
  setDistances,
  setPrev,
  setQueueSnapshot,
}) => {
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const positions = useMemo(() => {
    const map: Record<NodeId, { x: number; y: number }> = {};
    if (nodes.length === 0) return map;

    const centerX = 200;
    const centerY = 130;
    const radius = 110;

    nodes.forEach((node, index) => {
      const angle = (2 * Math.PI * index) / nodes.length - Math.PI / 2;
      map[node] = {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
      };
    });
    return map;
  }, [nodes]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  useEffect(() => {
    clearTimers();
    setHighlightedNodes([]);
    setDistances({});
    setPrev({});
    setQueueSnapshot([]);
  }, [resetSignal, setHighlightedNodes, setDistances, setPrev, setQueueSnapshot]);

  useEffect(() => {
    clearTimers();
    setHighlightedNodes([]);
    setQueueSnapshot([]);

    if (!startNode || nodes.length === 0) {
      setDistances({});
      setPrev({});
      return;
    }

    const adj = buildAdjacency(nodes, edges);
    const dist: Record<NodeId, number> = {};
    const prev: Record<NodeId, NodeId | null> = {};
    nodes.forEach((n) => {
      dist[n] = Number.POSITIVE_INFINITY;
      prev[n] = null;
    });
    dist[startNode] = 0;

    let queue: NodeId[] = [startNode];

    const steps: Array<() => void> = [];

    const pushSnapshot = () => {
      const distCopy: Record<NodeId, number> = {};
      nodes.forEach((n) => {
        distCopy[n] = dist[n];
      });

      const prevCopy: Record<NodeId, NodeId | null> = {};
      nodes.forEach((n) => {
        prevCopy[n] = prev[n];
      });

      const queueCopy = [...queue];

      steps.push(() => {
        setDistances(distCopy);
        setPrev(prevCopy);
        setQueueSnapshot(queueCopy);
      });
    };
    pushSnapshot();

    while (queue.length > 0) {
      let bestIndex = 0;
      for (let i = 1; i < queue.length; i++) {
        if (dist[queue[i]] < dist[queue[bestIndex]]) {
          bestIndex = i;
        }
      }
      const u = queue.splice(bestIndex, 1)[0];

      steps.push(() => {
        setHighlightedNodes((prevNodes) =>
          prevNodes.includes(u) ? prevNodes : [...prevNodes, u]
        );
      });

      const neighbors = adj[u] ?? [];
      for (const { node: v, weight } of neighbors) {
        const alt = dist[u] + weight;
        if (alt < dist[v]) {
          dist[v] = alt;
          prev[v] = u;

          if (!queue.includes(v)) {
            queue.push(v);
          }
          pushSnapshot();
        }
      }
    }

    const STEP_MS = 500;
    steps.forEach((fn, idx) => {
      const t = setTimeout(fn, idx * STEP_MS);
      timersRef.current.push(t);
    });

    const finalDist: Record<NodeId, number> = {};
    nodes.forEach((n) => {
      finalDist[n] = dist[n];
    });

    const finalPrev: Record<NodeId, NodeId | null> = {};
    nodes.forEach((n) => {
      finalPrev[n] = prev[n];
    });

    const t = setTimeout(() => {
      setDistances(finalDist);
      setPrev(finalPrev);
      setQueueSnapshot([]);
    }, steps.length * STEP_MS);
    timersRef.current.push(t);

    return clearTimers;
  }, [runSignal]);

  if (nodes.length === 0) {
    return <svg className={styles.graphSvg} viewBox="0 0 400 260" />;
  }

  return (
    <svg className={styles.graphSvg} viewBox="0 0 400 260">
      {edges.map((edge, idx) => {
        const p1 = positions[edge.from];
        const p2 = positions[edge.to];
        if (!p1 || !p2) return null;

        const midX = (p1.x + p2.x) / 2;
        const midY = (p1.y + p2.y) / 2;

        const weightStrokeWidth = 1 + edge.weight * 0.25;

        return (
          <g key={idx}>
            <line
              x1={p1.x}
              y1={p1.y}
              x2={p2.x}
              y2={p2.y}
              className={styles.graphEdge}
              style={{ strokeWidth: weightStrokeWidth }}
            />
            <text
              x={midX}
              y={midY - 4}
              className={styles.graphEdgeLabel}
            >
              {edge.weight}
            </text>
          </g>
        );
      })}

      {/* Nodes */}
      {nodes.map((node) => {
        const pos = positions[node];
        const isVisited = highlightedNodes.includes(node);
        const isOnShortestPath = shortestPathNodes.includes(node);

        let nodeClass = styles.graphNode;
        if (isVisited) {
          nodeClass = `${styles.graphNode} ${styles.graphNodeHighlighted}`;
        }
        if (isOnShortestPath) {
          nodeClass = `${nodeClass} ${styles.graphNodeShortestPath || ""}`;
        }

        return (
          <g key={node} transform={`translate(${pos.x}, ${pos.y})`}>
            <circle r={20} className={nodeClass} />
            <text dy="0.35em" className={styles.graphNodeLabel}>
              {node}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

export default DijkstraEngine;
